import {useMemo, useState} from "react";
import {RouteEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseRouteDataTableFilterProps = Omit<UseDataTableFilterProps<RouteEntity>, 'filterConfig'>

export function useRouteDataTableFilter(props: UseRouteDataTableFilterProps): UseDataTableFilterResult<RouteEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [groupQuery, setGroupQuery] = useState<string | undefined>(undefined);
  const [groupDebouncedTypeQuery] = useDebouncedValue(groupQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const filterConfig: DataTableFilterConfig<RouteEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "group": {
        setQuery: setGroupQuery,
        query: groupQuery,
        debouncedQuery: groupDebouncedTypeQuery,
        filter: (record, query) => !record.routeGroupName || !record.routeGroupName.toLowerCase().includes(query.toLowerCase())
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: (record, query) => !record.companyName || !record.companyName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    groupQuery,
    groupDebouncedTypeQuery,
    companyQuery,
    companyDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
